import React from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { getDreamCategory, getDreamSubject, getAllDreamCategories } from '../../data/dreamData';
import DreamBreadcrumb from '../../components/fortune/DreamBreadcrumb';
import AdBanner from '../../components/AdBanner';
import SEO from '../../components/SEO';
import './DreamCategoryPage.css';

function DreamSubjectPage() {
  const { categoryId, subjectId } = useParams();
  const category = getDreamCategory(categoryId);
  const subject = getDreamSubject(categoryId, subjectId);

  if (!category || !subject) {
    return <Navigate to={category ? `/fortune/dream/${categoryId}` : '/fortune/dream'} replace />;
  }

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    inLanguage: 'ko',
    author: { '@type': 'Organization', name: '로또운세' },
    mainEntity: subject.scenarios.map(scenario => ({
      '@type': 'Question',
      name: `${scenario.title}의 의미는?`,
      acceptedAnswer: {
        '@type': 'Answer',
        text: scenario.interpretation,
      },
    })),
  };

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: subject.seoTitle,
    description: subject.seoDescription,
    author: { '@type': 'Organization', name: '로또운세' },
    datePublished: '2025-01-01',
    inLanguage: 'ko',
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://lottounse.kr/fortune/dream/${categoryId}/${subjectId}`,
    },
  };

  const getOmenInfo = (omen) => {
    switch (omen) {
      case 'good': return { label: '길몽', emoji: '🎉', className: 'omen-good' };
      case 'bad': return { label: '흉몽', emoji: '⚠️', className: 'omen-bad' };
      default: return { label: '주의', emoji: '🔔', className: 'omen-caution' };
    }
  };

  // Related subjects from same category (excluding current)
  const relatedSubjects = category.subjects
    .filter(s => s.id !== subject.id)
    .slice(0, 4);

  // Cross-category related subjects
  const crossCategorySubjects = getAllDreamCategories()
    .filter(c => c.id !== categoryId)
    .flatMap(c => c.subjects.slice(0, 1).map(s => ({ ...s, categoryId: c.id, categoryName: c.name })))
    .slice(0, 4);

  const adInterval = 3;

  return (
    <div className="fortune-category-page">
      <SEO
        title={subject.seoTitle}
        description={subject.seoDescription}
        keywords={`${subject.name},꿈해몽,${subject.scenarios.map(s => s.title).slice(0, 5).join(',')}`}
        url={`https://lottounse.kr/fortune/dream/${categoryId}/${subjectId}`}
        type="article"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
      </Helmet>

      <div className="category-header dream-header">
        <h1>{subject.emoji} {subject.name} 해몽</h1>
        <p>{subject.summary}</p>
      </div>

      <div className="category-content">
        <DreamBreadcrumb category={category} subject={subject} />

        <AdBanner />

        <h2 className="section-title">{subject.emoji} {subject.name} - 상황별 해석</h2>

        <div className="scenario-list">
          {subject.scenarios.map((scenario, idx) => {
            const omenInfo = getOmenInfo(scenario.omen);
            return (
              <React.Fragment key={scenario.id}>
                <div className="scenario-item">
                  <div className="scenario-header">
                    <h3 className="scenario-title">{scenario.title}</h3>
                    <span className={`omen-badge ${omenInfo.className}`}>
                      {omenInfo.emoji} {omenInfo.label}
                    </span>
                  </div>
                  <p className="scenario-interpretation">{scenario.interpretation}</p>
                  <div className="scenario-footer">
                    <div className="lucky-numbers">
                      <span className="lucky-label">행운의 번호</span>
                      {scenario.luckyNumbers.map((num, i) => (
                        <span key={i} className="lucky-number">{num}</span>
                      ))}
                    </div>
                    <div className="scenario-tags">
                      {scenario.tags.map((tag, i) => (
                        <span key={i} className="scenario-tag">#{tag}</span>
                      ))}
                    </div>
                  </div>
                </div>
                {(idx + 1) % adInterval === 0 && idx < subject.scenarios.length - 1 && (
                  <AdBanner />
                )}
              </React.Fragment>
            );
          })}
        </div>

        <AdBanner />

        {relatedSubjects.length > 0 && (
          <div className="related-section">
            <h3 className="section-title">📌 관련 꿈해몽</h3>
            <div className="related-grid">
              {relatedSubjects.map(related => (
                <Link
                  key={related.id}
                  to={`/fortune/dream/${categoryId}/${related.id}`}
                  className="related-card"
                >
                  <span className="related-emoji">{related.emoji}</span>
                  <span className="related-name">{related.name}</span>
                </Link>
              ))}
            </div>
          </div>
        )}

        {crossCategorySubjects.length > 0 && (
          <div className="related-section">
            <h3 className="section-title">🔗 다른 카테고리 인기 꿈</h3>
            <div className="related-grid">
              {crossCategorySubjects.map(related => (
                <Link
                  key={`${related.categoryId}-${related.id}`}
                  to={`/fortune/dream/${related.categoryId}/${related.id}`}
                  className="related-card"
                >
                  <span className="related-emoji">{related.emoji}</span>
                  <span className="related-name">{related.name}</span>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default DreamSubjectPage;
